import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, MessageSquare } from "lucide-react";
import { PageHero } from "@/components/common/SectionHeading";
import SEO from "@/components/common/SEO";
import { fetchApplicantTypes } from "@/lib/lnlApi";

export default function Admissions() {
    const [types, setTypes] = useState([]);
    useEffect(() => { fetchApplicantTypes().then(setTypes); }, []);

    return (
        <div data-testid="admissions-page">
            <SEO
                title="Admissions — How to Apply"
                description="Start your Lyceum Northern Luzon application. Choose your path — Kindergarten, Elementary, JHS, SHS, College Freshman, Transferee, ALS/PEPT, or Foreign Student."
                path="/admissions"
            />
            <PageHero
                eyebrow="Admissions"
                title="Welcome to LNL Admissions"
                description="LNL welcomes prospective learners. Requirements may be updated — please confirm with the Admissions Office before submission."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Admissions" }]}
                cta={
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link to="/admissions/apply" className="lnl-btn-primary">Start Your Application <ArrowRight className="h-4 w-4" /></Link>
                        <Link to="/admissions/requirements" className="lnl-btn-secondary">View Admission Requirements</Link>
                        <Link to="/admissions/status" className="lnl-link inline-flex items-center py-2.5">Check application status</Link>
                    </div>
                }
            />
            <section className="py-14 bg-white" id="applicant-paths">
                <div className="lnl-container">
                    <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">Choose your applicant path</h2>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {types.map((t) => (
                            <Link key={t.code} to={`/admissions/${t.code}`} className="lnl-card p-5 hover:border-lnl-green/40" data-testid={`admissions-path-${t.code}`}>
                                <div className="lnl-eyebrow">{t.level.toUpperCase()}</div>
                                <div className="font-heading font-bold text-lg text-lnl-charcoal mt-1">{t.label}</div>
                                <div className="text-sm text-lnl-gray mt-2">{t.short}</div>
                                <div className="mt-4 text-xs text-lnl-green-deep font-heading font-semibold inline-flex items-center gap-1">View path <ArrowRight className="h-3.5 w-3.5" /></div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
            <section className="py-12 bg-lnl-off-white">
                <div className="lnl-container grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                        { icon: ClipboardCheck, title: "Requirements checklist", body: "Download a printable list of documents for your applicant type.", to: "/admissions/requirements", cta: "See requirements" },
                        { icon: MessageSquare, title: "Ask Admissions", body: "Send a short inquiry — you'll receive a unique reference number.", to: "/contact", cta: "Send inquiry" },
                        { icon: ArrowRight, title: "Start your application", body: "Save-and-continue application with document upload.", to: "/admissions/apply", cta: "Apply now" },
                    ].map((c) => (
                        <div key={c.title} className="lnl-card p-5">
                            <c.icon className="h-6 w-6 text-lnl-green" />
                            <div className="font-heading font-bold text-lnl-charcoal mt-3">{c.title}</div>
                            <div className="text-sm text-lnl-gray mt-1">{c.body}</div>
                            <Link to={c.to} className="lnl-link inline-flex items-center gap-1 mt-3 text-sm">{c.cta} <ArrowRight className="h-3.5 w-3.5" /></Link>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
